import React, {
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  useMemo,
  useLayoutEffect,
  useState,
} from 'react';

import { sanitizeUrl } from '@braintree/sanitize-url';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import styledC from 'styled-components';
import {
  space,
  type SpaceProps,
  color,
  type ColorProps,
  typography as typographySystem,
  type TypographyProps,
  system,
} from 'styled-system';

import A from '@adsk/alloy-react-a';
import { useForwardedRef } from '@adsk/alloy-react-helpers';
import { OpenIcon } from '@adsk/alloy-react-icon';

import {
  type Colors,
  type Typography,
  typography,
  propTypes as themePropTypes,
} from '../Theme';

export const textPropTypes = {
  ...propTypes.space,
  ...propTypes.color,
  ...propTypes.typography,
  ...themePropTypes.color,
  ...themePropTypes.typography,
  detectUrls: PropTypes.bool,
  showUrlsIcons: PropTypes.bool,
};

export interface ITextProps
  extends SpaceProps,
    TypographyProps,
    ColorProps,
    ComponentProps<'span'>,
    Pick<CSSProperties, 'textTransform' | 'whiteSpace' | 'wordBreak'> {
  typography?: Typography;
  color?: Colors;
  bg?: Colors;
  detectUrls?: boolean;
  showUrlsIcons?: boolean;
}

const getIconSize = (element: HTMLElement | null) => {
  if (!element) return 16;

  const computedStyle = window.getComputedStyle(element);
  const fontSize = parseFloat(computedStyle.fontSize);

  // For text bigger than 16pt (approximately 21.33px), use 24px icon
  return fontSize > 21.33 ? 24 : 16;
};

const LinkWithIcon = React.forwardRef<
  HTMLAnchorElement,
  {
    href: string;
    displayUrl: string;
    parentElement: HTMLElement | null;
    showIcon?: boolean;
  }
>(({ href, displayUrl, parentElement, showIcon = true, ...props }, ref) => {
  const [iconSize, setIconSize] = useState(16);

  useLayoutEffect(() => {
    setIconSize(getIconSize(parentElement));
  }, [parentElement]);

  return (
    <span style={{ display: 'inline', maxWidth: '100%' }}>
      <A
        ref={ref}
        href={href}
        target="_blank"
        style={{
          textDecoration: 'underline',
          fontWeight: 'normal',
          display: 'inline',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
        {...props}
      >
        {displayUrl}
        {showIcon && (
          <OpenIcon
            size={iconSize}
            data-testid="open-icon"
            style={{
              marginLeft: '2px',
              marginRight: '4px',
              position: 'relative',
              top: iconSize === 24 ? '5px' : '2px',
            }}
          />
        )}
      </A>
    </span>
  );
});

LinkWithIcon.displayName = 'LinkWithIcon';

const parseTextWithUrls = (
  text: string,
  parentElement: HTMLElement | null,
  showUrlsIcons: boolean,
) => {
  if (!text) return null;

  const urlRegex = /(https?:\/\/|www\.)[^\s]+/g;
  const parts = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  urlRegex.lastIndex = 0;

  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[0];
    const startIndex = match.index;

    if (startIndex > lastIndex) {
      parts.push(text.substring(lastIndex, startIndex));
    }

    const punctuationMatch = url.match(/[.,;:!?)]$/);
    let displayUrl = url;
    let trailingPunctuation = '';

    if (punctuationMatch) {
      trailingPunctuation = punctuationMatch[0];
      displayUrl = url.slice(0, -1);
    }

    let linkHref = displayUrl.startsWith('www.')
      ? `https://${displayUrl}`
      : displayUrl;

    // Always sanitize URLs
    linkHref = sanitizeUrl(linkHref);

    parts.push(
      <LinkWithIcon
        key={`link-${startIndex}`}
        href={linkHref}
        displayUrl={displayUrl}
        parentElement={parentElement}
        showIcon={showUrlsIcons}
      />,
    );

    if (trailingPunctuation) {
      parts.push(trailingPunctuation);
    }

    lastIndex = startIndex + url.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length ? parts : text;
};

const processMixedContent = (
  children: ReactNode,
  parentElement: HTMLElement | null,
  showUrlsIcons: boolean,
): ReactNode => {
  if (!children) return children;

  return React.Children.map(children, (child) =>
    typeof child === 'string'
      ? parseTextWithUrls(child, parentElement, showUrlsIcons)
      : child,
  );
};

const StyledText = styledC.span<ITextProps>(
  space,
  color,
  typography,
  typographySystem,
  system({
    textTransform: true,
    whiteSpace: true,
    wordBreak: true,
  }),
);

const Text = React.forwardRef<HTMLSpanElement, ITextProps>(
  (
    {
      children,
      detectUrls = true,
      showUrlsIcons = true,
      typography,
      style,
      ...props
    },
    forwardedRef,
  ) => {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const elementRef = useForwardedRef(forwardedRef);

    useLayoutEffect(() => {
      setElement(elementRef.current);
    }, [elementRef]);

    const processedTextContent = useMemo(() => {
      if (!detectUrls) return children;

      if (typeof children === 'string') {
        return parseTextWithUrls(children, element, showUrlsIcons);
      }

      if (React.isValidElement(children) || Array.isArray(children)) {
        return processMixedContent(children, element, showUrlsIcons);
      }

      return children;
    }, [children, detectUrls, element, showUrlsIcons]);

    const combinedStyle = useMemo(
      () => ({
        ...(typography === 'ellipsis' && {
          display: 'block',
        }),
        ...style,
      }),
      [typography, style],
    );

    return (
      <StyledText
        ref={elementRef}
        typography={typography}
        style={combinedStyle}
        {...props}
      >
        {processedTextContent}
      </StyledText>
    );
  },
);

Text.displayName = 'Text';
Text.propTypes = textPropTypes;

export default Text;
